/**
 * 단일 선택 목록의 viewport·행 생명주기·키보드/a11y·비동기 경계를 소유한다.
 *
 * 모든 행은 Annotation Studio에서 검증한 semantic list/listitem + primary button +
 * optional sibling action 계약을 쓴다. transport/search/filter/cache는 소비처 책임이다
 * (RFC-unified-list-view).
 */
import { isValidElement, useId, type ReactNode } from "react";

import { useDevWarnOnce } from "../../utils/useDevWarnOnce";
import { IconButton } from "../IconButton";
import { Scrollbar } from "../Scrollbar";
import { Spinner } from "../Spinner";
import { Text, defaultTextVariants, type TextVariantDefinition } from "../Text";
import { isTextNode } from "../../utils/reactNode";
import { SrOnly } from "../foundations/srOnly";
import {
  ListViewShell,
  resolveListViewRowGeometry,
  type ListViewCssProperties,
} from "./listViewShell";
import { useListViewCollections, type ListViewEntry } from "./useListViewCollections";
import { useListViewLoadBoundary, prefetchIndex } from "./useListViewLoadBoundary";
import { useListViewNavigation } from "./useListViewNavigation";
import { useListViewSelection, type ListViewSelectHandler } from "./useListViewSelection";
import {
  useListViewViewport,
  getListViewVirtualRange,
  DEFAULT_OVERSCAN,
  type ListViewVirtualRange,
} from "./useListViewViewport";

export { prefetchIndex, getListViewVirtualRange };
export type { ListViewVirtualRange };

export type ListViewVariant = "comfortable" | "compact";
export type ListViewAppearance = "framed" | "embedded";
export type ListViewRowAppearance = "continuous" | "raised";
export type ListViewRowPaddingInline = 8 | 16;
export type ListViewStatus = "idle" | "loading" | "success" | "error";

export type ListViewVirtualization = {
  /** viewport 앞뒤에 추가로 mount할 행 수. 기본 5. 높이는 variant, 간격은 rowAppearance가 소유한다. */
  overscan?: number;
};

// item builder가 화면을 variant에 맞게 그릴 때 참고하는 밀도 토큰.
export type ListViewItemTheme = {
  variant: ListViewVariant;
  density: ListViewVariant;
  // 콘텐츠(예: 제목/메타) 세로 간격 권장값(px). 옵션 래퍼 패딩은 ListView가 소유한다.
  gap: number;
  titleTextVariant: TextVariantDefinition;
  metaTextVariant: TextVariantDefinition;
};

export type ListViewItemContext = {
  selected: boolean;
  /** 정규 행은 `items` 인덱스, pinned 행은 `pinned.items`의 원래 인덱스(dedupe 무관). */
  index: number;
  theme: ListViewItemTheme;
  /** 현재 결과 밖에 유지되는 pinned group의 행인지 여부. runtime에는 항상 주입된다. */
  pinned?: boolean;
};

/**
 * `items`가 더 큰 결과 집합의 창(window)이라는 사실. 가상화 여부와 독립인
 * 결과 집합 메타데이터로, ARIA `aria-setsize`/`aria-posinset` 계산에만 쓴다.
 */
export type ListViewResultWindow = {
  /** 전체 결과 수. 모르면 생략 — virtualized set은 aria-setsize=-1을 쓴다. */
  total?: number;
  /** items[0]의 전체 결과 내 0-based 위치. 기본 0. */
  offset?: number;
};

/** 현재 결과 밖에서도 같은 scroller의 선두 그룹에 보존하는 collection 계약. */
export type ListViewPinned<T> = {
  /**
   * 결과 앞에 유지할 항목. `items`와 같은 key는 렌더에서 제외하되,
   * onSelect/context의 index는 이 배열의 원래 위치를 유지한다.
   */
  items: readonly T[];
  /** 화면에는 숨기고 pinned collection의 accessible name으로 사용하는 설명. */
  label?: ReactNode;
};

/**
 * renderItem이 반환하는 행 구성. `content`는 non-interactive 시각 콘텐츠,
 * `actions`는 primary button과 DOM sibling으로 렌더할 인터랙티브 노드다.
 * 인터랙티브 노드가 없으면 descriptor 대신 ReactNode를 그대로 반환한다.
 */
export type ListViewRenderedRow = {
  content: ReactNode;
  actions?: ReactNode;
};

// ReactNode도 object일 수 있으므로(element/array) descriptor는 그 둘을 배제하고 판별한다.
function resolveRenderedRow(rendered: ReactNode | ListViewRenderedRow): ListViewRenderedRow {
  if (
    typeof rendered === "object" &&
    rendered !== null &&
    !isValidElement(rendered) &&
    !Array.isArray(rendered) &&
    "content" in rendered
  ) {
    return rendered;
  }
  return { content: rendered as ReactNode };
}

export type ListViewProps<T> = {
  items: readonly T[];
  getKey: (item: T) => string;
  selectedKey: string | null;
  /**
   * 선택 변경. `detail.reason`은 ↑/↓/Home/End 이동이면 `navigation`, primary click(포인터·
   * Enter/Space native activation)이면 `activation`이다. 이미 선택된 행의 activation도 호출한다.
   */
  onSelect: ListViewSelectHandler<T>;
  /** Pointer double-click 보조 activation. keyboard action은 명시적 action으로 제공한다. */
  onItemDoubleClick?: (item: T, index: number) => void;
  /**
   * 한 행의 구성. non-interactive 콘텐츠만이면 ReactNode를, 인터랙티브 action이
   * 필요하면 `{ content, actions }` descriptor를 반환한다 — actions는 ListView가
   * primary button의 DOM sibling cell로 렌더해 행 선택과 이벤트를 격리한다.
   */
  renderItem: (item: T, context: ListViewItemContext) => ReactNode | ListViewRenderedRow;
  variant?: ListViewVariant;
  /** 목록 프레임. embedded는 외곽선·radius·배경을 부모 surface에 맡긴다. */
  appearance?: ListViewAppearance;
  /** 연속 행 또는 2px 간격과 surface radius를 가진 개별 행. 기본 continuous. */
  rowAppearance?: ListViewRowAppearance;
  /** 헤더와 행 콘텐츠의 좌우 padding(px). 기본 16. sibling actions는 행 끝에 붙는다. */
  rowPaddingInline?: ListViewRowPaddingInline;

  /** 현재 결과 밖 선택 등을 같은 scroller 선두에 보존하는 pinned collection. */
  pinned?: ListViewPinned<T>;

  /**
   * variant 고정 높이(comfortable 45px / compact 32px)의 균일한 행만 windowing한다.
   * raised의 2px 행 간격은 행 높이 밖에 더한다.
   * 가변 높이 행에서는 생략한다. viewport 높이는 래퍼(소비처)가 소유한다.
   */
  virtualization?: ListViewVirtualization;
  /** items가 더 큰 결과 집합의 창일 때의 메타(서버 페이징 등) — ARIA 위치 전용. */
  resultWindow?: ListViewResultWindow;

  // first-page 로드 상태. items는 success일 때만 채워 넘긴다.
  status?: ListViewStatus;
  errorMessage?: string | null;
  /** 빈 결과. 글자는 c6 가 문단으로 그리고, 요소 노드는 문단·여백·면 없이 그대로 둔다(배치는 노드 소유). */
  emptyMessage?: ReactNode;
  onRetry?: () => void;
  retryLabel?: ReactNode;

  // 무한 스크롤. onLoadMore가 없으면 페이지네이션은 비활성.
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  // 다음 페이지를 미리 당기는 정도(0~1). 스크롤과 키보드가 같은 요청 latch를 공유한다.
  prefetchThreshold?: number;

  header?: ReactNode;
  // collection의 accessible name. 기본 "List".
  ariaLabel?: string;
  // ↑/↓·Home/End 화살표 탐색 사용 여부. 기본 true.
  keyboardNavigation?: boolean;
  className?: string;
};

const listViewDensityTokens: Record<
  ListViewVariant,
  Omit<ListViewItemTheme, "variant" | "density">
> = {
  comfortable: {
    gap: 2,
    titleTextVariant: defaultTextVariants["body-bold"],
    metaTextVariant: defaultTextVariants["caption-normal"],
  },
  compact: {
    gap: 0,
    titleTextVariant: defaultTextVariants["caption-bold"],
    metaTextVariant: defaultTextVariants["caption-normal"],
  },
};

function buildItemTheme(variant: ListViewVariant): ListViewItemTheme {
  return { variant, density: variant, ...listViewDensityTokens[variant] };
}

export function ListView<T>({
  items,
  getKey,
  selectedKey,
  onSelect,
  onItemDoubleClick,
  renderItem,
  variant = "comfortable",
  appearance = "framed",
  rowAppearance = "continuous",
  rowPaddingInline = 16,
  pinned,
  virtualization,
  resultWindow,
  status = "success",
  errorMessage,
  emptyMessage = "No items.",
  onRetry,
  retryLabel = "Retry",
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  prefetchThreshold = 0.8,
  header,
  ariaLabel = "List",
  keyboardNavigation = true,
  className,
}: ListViewProps<T>) {
  const pinnedLabelId = useId();
  const contentIdPrefix = useId();
  const theme = buildItemTheme(variant);

  const virtualized = virtualization !== undefined;
  const geometry = virtualized ? resolveListViewRowGeometry(variant, rowAppearance) : null;
  const virtualOverscan =
    virtualized && Number.isFinite(virtualization.overscan)
      ? Math.max(0, Math.floor(virtualization.overscan ?? DEFAULT_OVERSCAN))
      : DEFAULT_OVERSCAN;
  const resultOffset = resultWindow?.offset ?? 0;
  const resultTotal = resultWindow?.total;
  const normalizedItemOffset = Number.isFinite(resultOffset)
    ? Math.max(0, Math.floor(resultOffset))
    : 0;
  const minimumTotalCount = normalizedItemOffset + items.length;
  const normalizedTotalCount =
    resultTotal === undefined || !Number.isFinite(resultTotal) || resultTotal < 0
      ? undefined
      : Math.max(minimumTotalCount, Math.floor(resultTotal));

  useDevWarnOnce(
    resultOffset < 0 || !Number.isFinite(resultOffset),
    "[c6] <ListView resultWindow.offset> must be a non-negative finite number. It is normalized to 0.",
  );
  useDevWarnOnce(
    resultTotal !== undefined && (!Number.isFinite(resultTotal) || resultTotal < minimumTotalCount),
    "[c6] <ListView resultWindow.total> cannot be smaller than offset + items.length. ARIA set size is normalized.",
  );

  const pinnedLabel = pinned?.label ?? "Outside current results";
  const { pinnedEntries, regularEntries, navigationEntries } = useListViewCollections({
    items,
    getKey,
    pinnedItems: pinned?.items ?? [],
    regularNavigable: status === "success",
  });

  const selectedNavigationIndex = navigationEntries.findIndex((entry) => entry.key === selectedKey);
  const selectedEntry =
    selectedNavigationIndex >= 0 ? navigationEntries[selectedNavigationIndex] : undefined;
  const tabStopEntry = selectedEntry ?? navigationEntries[0];
  const loadedCount = status === "success" ? items.length : 0;

  const {
    scrollerRef,
    handleScroll,
    visibleRegularIndexes,
    scrollVirtualIndexIntoView,
    scrollMountedItemIntoView,
  } = useListViewViewport({
    geometry,
    overscan: virtualOverscan,
    pinnedCount: pinnedEntries.length,
    regularCount: regularEntries.length,
    tabStopEntry,
  });

  const { triggerRef, requestLoadMore, triggerIndex } = useListViewLoadBoundary({
    itemCount: items.length,
    loadedCount,
    hasMore,
    loadingMore,
    onLoadMore,
    prefetchThreshold,
    scrollerRef,
  });

  const select = useListViewSelection(onSelect);

  const { registerItem, handleItemKeyDown } = useListViewNavigation({
    navigationEntries,
    selectedNavigationIndex,
    selectedEntry,
    keyboardNavigation,
    select,
    virtualized,
    triggerIndex,
    requestLoadMore,
    scrollVirtualIndexIntoView,
    scrollMountedItemIntoView,
  });

  const shellStyle: ListViewCssProperties = {};
  if (geometry) {
    shellStyle["--c6-list-view-row-height"] = `${geometry.rowHeight}px`;
    shellStyle["--c6-list-view-virtual-height"] =
      `${Math.max(0, regularEntries.length * geometry.rowPitch - geometry.rowGap)}px`;
  }

  function itemContext(entry: ListViewEntry<T>): ListViewItemContext {
    return {
      selected: entry.key === selectedKey,
      index: entry.index,
      theme,
      pinned: entry.pinned,
    };
  }

  function ariaPosition(entry: ListViewEntry<T>) {
    if (entry.pinned) {
      // ARIA 위치는 렌더된 pinned 집합(1..N) 기준 — 소비처 index 네임스페이스와 분리.
      return { setSize: pinnedEntries.length, posInSet: entry.order + 1 };
    }
    const partial = virtualized || normalizedTotalCount !== undefined || normalizedItemOffset > 0;
    return {
      setSize: partial ? (normalizedTotalCount ?? -1) : undefined,
      posInSet: partial ? normalizedItemOffset + entry.index + 1 : undefined,
    };
  }

  function rowStyle(entry: ListViewEntry<T>): ListViewCssProperties | undefined {
    if (!geometry || entry.pinned) return undefined;
    return {
      "--c6-list-view-row-offset": `${entry.index * geometry.rowPitch}px`,
      height: geometry.rowHeight,
    };
  }

  function renderRow(entry: ListViewEntry<T>) {
    const selected = entry.key === selectedKey;
    const context = itemContext(entry);
    const position = ariaPosition(entry);
    const globalIndex = entry.pinned ? entry.order : pinnedEntries.length + entry.order;
    const className = `item${virtualized && !entry.pinned ? " virtualItem" : ""}`;
    const contentId = `${contentIdPrefix}-${entry.pinned ? "p" : "r"}-${entry.order}`;
    const last = entry.pinned
      ? entry.order === pinnedEntries.length - 1
      : entry.order === regularEntries.length - 1;
    const row = resolveRenderedRow(renderItem(entry.item, context));

    return (
      <li
        key={entry.key}
        aria-setsize={position.setSize}
        aria-posinset={position.posInSet}
        className={className}
        data-selected={selected ? "true" : undefined}
        data-pinned={entry.pinned ? "true" : undefined}
        data-last={last ? "true" : undefined}
        style={rowStyle(entry)}
      >
        <div className="primaryCell">
          <button
            ref={(element) => registerItem(entry.key, element)}
            type="button"
            className="primary"
            aria-labelledby={contentId}
            aria-pressed={selected}
            tabIndex={entry.key === tabStopEntry?.key ? 0 : -1}
            onClick={() => select(entry, "activation")}
            onDoubleClick={
              onItemDoubleClick ? () => onItemDoubleClick(entry.item, entry.index) : undefined
            }
            onKeyDown={(event) => handleItemKeyDown(event, globalIndex)}
          />
          <div id={contentId} className="content" aria-hidden="true">
            {row.content}
          </div>
        </div>
        {row.actions !== undefined && row.actions !== null ? (
          <div className="actions">{row.actions}</div>
        ) : null}
      </li>
    );
  }

  function renderCollection(entries: readonly ListViewEntry<T>[], pinned: boolean) {
    const labelProps = pinned
      ? { "aria-labelledby": pinnedLabelId }
      : { "aria-label": ariaLabel, "aria-busy": loadingMore || undefined };
    const collectionClass = `collection${virtualized && !pinned ? " virtualCollection" : ""}`;
    return (
      <ul className={collectionClass} {...labelProps}>
        {entries.map(renderRow)}
      </ul>
    );
  }

  const visibleRegularEntries = virtualized
    ? visibleRegularIndexes
        .map((index) => regularEntries[index])
        .filter((entry): entry is ListViewEntry<T> => entry !== undefined)
    : regularEntries;

  return (
    <ListViewShell
      $appearance={appearance}
      $rowAppearance={rowAppearance}
      $rowPaddingInline={rowPaddingInline}
      className={className}
      data-variant={variant}
      data-appearance={appearance}
      data-row-appearance={rowAppearance}
      data-virtualized={virtualized ? "true" : undefined}
      style={shellStyle}
    >
      {header ? <div className="header">{header}</div> : null}
      <Scrollbar
        ref={scrollerRef}
        className="scroller"
        gutter="auto"
        onScroll={virtualized ? handleScroll : undefined}
      >
        {pinnedEntries.length > 0 ? (
          <div className="pinnedGroup">
            <SrOnly as="div" id={pinnedLabelId}>
              {pinnedLabel}
            </SrOnly>
            {renderCollection(pinnedEntries, true)}
          </div>
        ) : null}
        {status === "loading" || status === "idle" ? (
          <div className="loader">
            <Spinner size="md" />
          </div>
        ) : null}
        {status === "error" ? (
          <ListViewMessage
            tone="error"
            action={
              onRetry ? (
                <div className="messageAction">
                  <IconButton text={retryLabel} variant="surface" size="sm" onClick={onRetry} />
                </div>
              ) : null
            }
          >
            {errorMessage}
          </ListViewMessage>
        ) : null}
        {status === "success" && items.length === 0 ? (
          isTextNode(emptyMessage) ? (
            <ListViewMessage>{emptyMessage}</ListViewMessage>
          ) : (
            // 요소 노드는 div·button 을 품을 수 있어 문단(Text) 밖에, 여백 없이 둔다.
            <div className="messageSlot">{emptyMessage}</div>
          )
        ) : null}
        {status === "success" && items.length > 0
          ? renderCollection(visibleRegularEntries, false)
          : null}
        {status === "success" && hasMore && onLoadMore ? (
          <div ref={triggerRef} className="loadTrigger" aria-hidden="true" />
        ) : null}
        {status === "success" && loadingMore ? (
          <div className="sentinel" aria-hidden="true">
            <Spinner size="sm" />
          </div>
        ) : null}
      </Scrollbar>
    </ListViewShell>
  );
}

function ListViewMessage({
  children,
  tone,
  action,
}: {
  children: ReactNode;
  tone?: "error";
  action?: ReactNode;
}) {
  return (
    <div className="message" data-tone={tone} role={tone === "error" ? "alert" : undefined}>
      <Text variant={defaultTextVariants["body-normal"]}>{children}</Text>
      {action}
    </div>
  );
}
